import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { api as aiStreamingAssistantApi } from '../../../actions/AiStreamingAssistant/aiStreamingAssistantActions';
import { loadDraft, saveDraft, loadFontSize, saveFontSize } from '../../../utils/AiStreamingAssistant/aiStreamingAssistantStorage';
import ClearHistoryIcon from '../../../../assets/svg/ai-clear-history.svg';
import './aiStreamingAssistantSidePanel.scss';

const FONT_SIZES = [13, 16, 18];
const GROUP_THRESHOLD_MS = 2 * 60 * 1000;

const formatTime = (timestamp) => {
  return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDateLabel = (timestamp) => {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }
  return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
};

const isLastInGroup = (message, nextMessage) => {
  if (!nextMessage) {
    return true;
  }
  if (nextMessage.get('role') !== message.get('role')) {
    return true;
  }
  const gap = (nextMessage.get('timestamp') || 0) - (message.get('timestamp') || 0);
  return gap > GROUP_THRESHOLD_MS;
};

export default function AiStreamingAssistantSidePanel({ sData, showSidePanel }) {
  const [inputText, setInputText] = useState('');
  const [fontSize, setFontSize] = useState(() => {
    const stored = loadFontSize();
    return FONT_SIZES.includes(stored) ? stored : FONT_SIZES[0];
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const userClickedDuringLoadingRef = useRef(false);

  const messages = sData.get('messages');
  const loading = sData.get('loading');
  const streamingStatus = sData.get('streamingStatus');

  useEffect(() => {
    if (!showSidePanel || !messagesEndRef.current) {
      return;
    }
    messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [showSidePanel, messages, loading]);

  // Track whether user clicked somewhere while loading, to avoid stealing focus.
  useEffect(() => {
    if (!loading) {
      return;
    }
    userClickedDuringLoadingRef.current = false;
    const handleMouseDown = () => { userClickedDuringLoadingRef.current = true; };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [loading]);

  // When loading finishes, refocus the input if the user hasn't clicked elsewhere.
  useEffect(() => {
    if (loading) {
      return;
    }
    const timer = setTimeout(() => {
      if (inputRef.current && !userClickedDuringLoadingRef.current) {
        inputRef.current.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [loading]);

  // When the panel opens, load the draft that may have been left by the chat dialog.
  useEffect(() => {
    if (!showSidePanel) {
      return;
    }
    setInputText(loadDraft());
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [showSidePanel]);

  const setInput = (text) => {
    setInputText(text);
    saveDraft(text);
  };

  const handleSend = () => {
    const text = inputText.trim();
    if (!text || loading) {
      return;
    }
    setInput('');
    aiStreamingAssistantApi.sendMessage(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSend();
    }
  };

  const handleFontDecrease = () => {
    const idx = FONT_SIZES.indexOf(fontSize);
    if (idx <= 0) {
      return;
    }
    const newSize = FONT_SIZES[idx - 1];
    setFontSize(newSize);
    saveFontSize(newSize);
  };

  const handleFontIncrease = () => {
    const idx = FONT_SIZES.indexOf(fontSize);
    if (idx >= FONT_SIZES.length - 1) {
      return;
    }
    const newSize = FONT_SIZES[idx + 1];
    setFontSize(newSize);
    saveFontSize(newSize);
  };

  const renderMessage = (message, index) => {
    const role = message.get('role');
    const text = message.get('text');
    const timestamp = message.get('timestamp');
    const isStreaming = message.get('streaming');

    const prevTimestamp = index > 0 ? messages.get(index - 1).get('timestamp') : null;
    const nextMessage = messages.get(index + 1);

    const showDateSeparator = timestamp && (
      !prevTimestamp ||
      new Date(timestamp).toDateString() !== new Date(prevTimestamp).toDateString()
    );
    const showTime = timestamp && !isStreaming && isLastInGroup(message, nextMessage);

    if (isStreaming && !text) {
      return null;
    }

    return (
      <React.Fragment key={index}>
        {showDateSeparator && (
          <div className="ais-sp-date-separator">
            <span>{formatDateLabel(timestamp)}</span>
          </div>
        )}
        <div className={`ais-sp-message ais-sp-message--${role}`}>
          <div className="ais-sp-message-content">
            <div className="ais-sp-bubble">
              {role === 'assistant' ? (
                <>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
                  {isStreaming && text && <span className="ais-streaming-cursor" />}
                </>
              ) : (
                text
              )}
            </div>
            {showTime && (
              <span
                className="ais-sp-message-time"
                title={new Date(timestamp).toLocaleString()}
              >
                {formatTime(timestamp)}
              </span>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  };

  const renderLoadingIndicator = () => {
    if (!loading) {
      return null;
    }
    // Once the streaming message has content, hide the typing dots
    const lastMessage = messages.last();
    if (lastMessage && lastMessage.get('streaming') && lastMessage.get('text')) {
      return null;
    }
    return (
      <div className="ais-sp-message ais-sp-message--assistant">
        <div className="ais-sp-bubble ais-sp-bubble--typing">
          <span className="ais-sp-typing-dot" />
          <span className="ais-sp-typing-dot" />
          <span className="ais-sp-typing-dot" />
          {streamingStatus && (
            <>
              <span className="ais-sp-status-text">{streamingStatus}</span>
              <span className="ais-streaming-cursor" />
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`ais-assistant-side-panel${showSidePanel ? ' open' : ''}`}>
      <div className="ais-sp-inner">
        <div className="ais-sp-header">
          <span className="ais-sp-title">Plato</span>
          <div className="ais-sp-toolbar">
            <button
              className="ais-sp-toolbar-btn ais-sp-toolbar-btn--font"
              onClick={handleFontDecrease}
              disabled={FONT_SIZES.indexOf(fontSize) === 0}
              title="Decrease font size"
              aria-label="Decrease font size"
            >
              A-
            </button>
            <button
              className="ais-sp-toolbar-btn ais-sp-toolbar-btn--font"
              onClick={handleFontIncrease}
              disabled={FONT_SIZES.indexOf(fontSize) === FONT_SIZES.length - 1}
              title="Increase font size"
              aria-label="Increase font size"
            >
              A+
            </button>
            <div className="ais-sp-toolbar-spacer" />
            <button
              className="ais-sp-toolbar-btn"
              onClick={() => aiStreamingAssistantApi.startNewChat()}
              title="Clear chat history"
              aria-label="Clear chat history"
            >
              <ClearHistoryIcon />
            </button>
          </div>
          <button className="ais-sp-close" onClick={() => aiStreamingAssistantApi.hideSidePanel()} aria-label="Close">
            ×
          </button>
        </div>

        <div className="ais-sp-messages" style={{ fontSize: `${fontSize}px` }}>
          {messages.size === 0 && (
            <div className="ais-sp-empty">How can I help you today?</div>
          )}
          {messages.map((message, index) => renderMessage(message, index))}
          {renderLoadingIndicator()}
          <div ref={messagesEndRef} />
        </div>

        <div className="ais-sp-input-area">
          <textarea
            ref={inputRef}
            className="ais-sp-input"
            style={{ fontSize: `${fontSize}px` }}
            value={inputText}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message... (Ctrl+Enter to send)"
            rows={3}
            disabled={loading}
          />
          <button
            className="ais-sp-send-btn"
            onClick={handleSend}
            disabled={!inputText.trim() || loading}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

AiStreamingAssistantSidePanel.propTypes = {
  sData: PropTypes.object.isRequired,
  showSidePanel: PropTypes.bool,
};

AiStreamingAssistantSidePanel.defaultProps = {
  showSidePanel: false,
};
